/**
 * <DrawingPhaseTimer>: V4.2.T7.
 *
 * The prominent in-game countdown chip for the drawing phase's top band:
 * a clock icon + a big, bold, tabular-nums second count, driven by a 3-stop
 * urgency ramp (calm → warning → critical) keyed on the fraction of time
 * remaining (`timerStop`). The critical stop pulses; a reduced-motion static
 * style and the seconds===0 HOLD both stop the pulse in CSS, not here.
 *
 * Accessibility (C9, WCAG 1.4.1):
 *   - Urgency is signalled by a visible icon/text cue, not color alone.
 *   - The ticking number is aria-hidden (NOT a polite live region), so screen
 *     readers don't re-read it every second.
 *   - A separate visually-hidden polite <span role="status"> announces the
 *     STOP (not the raw count), so it fires only at threshold crossings.
 */
import { timerStop, type TimerStop } from "../game/timerRamp.js";

/** Visible non-color cue per stop (the WCAG 1.4.1 "not color alone" signal). */
const CUE_TEXT: Record<TimerStop, string> = {
  calm: "",
  warning: "Hurry",
  critical: "Time's almost up!",
};

/** Polite screen-reader status per stop, announced only at threshold change. */
const STATUS_TEXT: Record<TimerStop, string> = {
  calm: "Plenty of time left",
  warning: "Time is running low",
  critical: "Time is almost up",
};

type DrawingPhaseTimerProps = {
  secondsLeft: number;
  /** Phase total in seconds; undefined → calm fallback (no false alarm). */
  totalSeconds: number | undefined;
  /**
   * Q9 (audit 2026-06-11): true during the drawer-disconnect grace window.
   * The server cancels its phase timer the moment the drawer drops, so a
   * countdown still ticking here is a lie, and it competed with the grace
   * banner's own countdown (two different numbers racing). Paused shows a
   * static ⏸ chip instead.
   */
  paused?: boolean;
};

export function DrawingPhaseTimer({
  secondsLeft,
  totalSeconds,
  paused = false,
}: DrawingPhaseTimerProps) {
  if (paused) {
    return (
      <span
        class="drawing-phase-timer drawing-phase-timer--paused"
        data-testid="drawing-phase-timer"
        data-stop="paused"
      >
        <span
          class="drawing-phase-timer-icon"
          data-testid="drawing-phase-timer-icon"
          aria-hidden="true"
        >
          ⏸
        </span>
        <span
          class="drawing-phase-timer-value"
          data-testid="drawing-phase-timer-value"
          aria-hidden="true"
        >
          paused
        </span>
        <span
          class="drawing-phase-timer-status"
          data-testid="drawing-phase-timer-status"
          role="status"
          aria-live="polite"
        >
          Timer paused while the drawer reconnects
        </span>
      </span>
    );
  }
  const stop = timerStop(secondsLeft, totalSeconds ?? 0);
  const cue = CUE_TEXT[stop];
  return (
    <span
      class={`drawing-phase-timer drawing-phase-timer--${stop === "critical" ? "urgent" : stop}`}
      data-testid="drawing-phase-timer"
      data-stop={stop}
      data-seconds-left={secondsLeft}
    >
      <span
        class="drawing-phase-timer-icon"
        data-testid="drawing-phase-timer-icon"
        aria-hidden="true"
      >
        ⏱
      </span>
      <span
        class="drawing-phase-timer-value"
        data-testid="drawing-phase-timer-value"
        aria-hidden="true"
      >
        {secondsLeft}s
      </span>
      {cue ? (
        <span
          class="drawing-phase-timer-cue"
          data-testid="drawing-phase-timer-cue"
          aria-hidden="true"
        >
          {cue}
        </span>
      ) : (
        <span class="drawing-phase-timer-cue" data-testid="drawing-phase-timer-cue" hidden />
      )}
      <span
        class="drawing-phase-timer-status"
        data-testid="drawing-phase-timer-status"
        role="status"
        aria-live="polite"
      >
        {STATUS_TEXT[stop]}
      </span>
    </span>
  );
}
